import type { User, Post, Zip, Spot, JamSesh, Event, UserStory } from '../types';

// Persists the last successful boot fetch so a relaunch can paint real
// content immediately (stale-while-revalidate) instead of sitting on the
// boot screen every time, even on a fast connection. fetchAllData() always
// still runs afterward to revalidate against Firestore - this is a pure
// perf optimization, never the source of truth.
const CACHE_KEY = 'ridaz_boot_data_cache_v1';
const CACHE_MAX_AGE_MS = 24 * 60 * 60 * 1000;

interface CachedBootData {
  savedAt: number;
  users: User[];
  posts: Post[];
  zips: Zip[];
  spots: Spot[];
  jamSeshes: JamSesh[];
  events: Event[];
  stories: UserStory[];
}

export const loadCachedBootData = (): CachedBootData | null => {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CachedBootData;
    if (!parsed || typeof parsed.savedAt !== 'number') return null;
    if (Date.now() - parsed.savedAt > CACHE_MAX_AGE_MS) return null;
    return parsed;
  } catch {
    return null;
  }
};

export const saveCachedBootData = (data: Omit<CachedBootData, 'savedAt'>): void => {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify({ ...data, savedAt: Date.now() }));
  } catch {
    // Quota exceeded or storage unavailable - caching is a pure optimization, safe to skip.
  }
};
